'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import { useBuilderStore } from '@/store/builderStore';
import Button from '@/app/components/ui/Button';
import {
  ClipboardCheck,
  ArrowLeft,
  Send,
  User,
  Building2,
  Mail,
  Phone,
  Globe,
  Package,
  Layers,
  Ruler,
  Image as ImageIcon,
  AlertCircle,
} from 'lucide-react';
import Image from 'next/image';

interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function InfoRow({ icon, label, value }: InfoRowProps) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-slate-50 last:border-0">
      <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center shrink-0 mt-0.5">
        <span className="text-amber-500">{icon}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">
          {label}
        </p>
        <p className="text-sm text-slate-800 font-medium leading-relaxed">{value}</p>
      </div>
    </div>
  );
}

interface SectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

function Section({ title, icon, children }: SectionProps) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-slate-800 to-slate-900">
        <span className="text-amber-400">{icon}</span>
        <h3 className="text-sm font-bold text-white uppercase tracking-wider">{title}</h3>
      </div>
      <div className="px-5 py-2">{children}</div>
    </div>
  );
}

export default function Review() {
  const {
    customer,
    product,
    material,
    dimensions,
    referenceImage,
    referenceImageName,
    generatedImages,
    selectedImageIndex,
    isSubmitting,
    setIsSubmitting,
    setIsSuccess,
    setCurrentStep,
  } = useBuilderStore();

  const [error, setError] = useState<string | null>(null);

  const selectedImage =
    generatedImages.length > 0 ? generatedImages[selectedImageIndex] : null;

  const isImageFile =
    referenceImage && !referenceImageName?.endsWith('.pdf');

  const handleSubmit = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      const response = await fetch('/api/send-mail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer,
          product,
          material,
          dimensions,
          referenceImage,
          referenceImageName,
          selectedImage,
          userAgent: navigator.userAgent,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send inquiry');
      }

      setIsSuccess(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to send inquiry. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -24 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/25">
            <ClipboardCheck size={18} className="text-white" />
          </div>
          <div>
            <h2 className="text-lg font-black text-slate-800 tracking-tight">Review & Submit</h2>
            <p className="text-sm text-slate-400">
              Review your inquiry before sending
            </p>
          </div>
        </div>

        {/* Customer Details */}
        <Section title="Customer Details" icon={<User size={16} />}>
          <InfoRow icon={<User size={14} />} label="Full Name" value={customer.customerName} />
          <InfoRow
            icon={<Building2 size={14} />}
            label="Company"
            value={customer.companyName}
          />
          <InfoRow icon={<Mail size={14} />} label="Email" value={customer.email} />
          <InfoRow icon={<Phone size={14} />} label="Phone" value={customer.phone} />
          <InfoRow icon={<Globe size={14} />} label="Country" value={customer.country} />
        </Section>

        {/* Product Details */}
        <Section title="Product Details" icon={<Package size={16} />}>
          <InfoRow
            icon={<Package size={14} />}
            label="Product Name"
            value={product.productName}
          />
          <InfoRow
            icon={<ClipboardCheck size={14} />}
            label="Description"
            value={product.description}
          />
        </Section>

        {/* Material & Dimensions */}
        <Section title="Material & Dimensions" icon={<Layers size={16} />}>
          <InfoRow icon={<Layers size={14} />} label="Material" value={material.material} />
          <InfoRow icon={<Layers size={14} />} label="Finish" value={material.finish} />
          <InfoRow icon={<Layers size={14} />} label="Color" value={material.color} />
          <InfoRow
            icon={<Ruler size={14} />}
            label="Dimensions (H × W × D)"
            value={
              [dimensions.height, dimensions.width, dimensions.depth]
                .filter(Boolean)
                .map((v) => `${v} cm`)
                .join(' × ') || '—'
            }
          />
          <InfoRow
            icon={<Ruler size={14} />}
            label="Weight"
            value={dimensions.weight ? `${dimensions.weight} kg` : ''}
          />
        </Section>

        {/* Reference Image */}
        {referenceImage && (
          <Section title="Reference Image" icon={<ImageIcon size={16} />}>
            <div className="py-4">
              {isImageFile ? (
                <div className="relative w-full h-48 rounded-xl overflow-hidden bg-slate-100">
                  <Image
                    src={referenceImage}
                    alt="Reference"
                    fill
                    className="object-contain"
                    unoptimized
                  />
                </div>
              ) : (
                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                  <span className="text-2xl">📄</span>
                  <div>
                    <p className="text-sm font-semibold text-slate-700">{referenceImageName}</p>
                    <p className="text-xs text-slate-400">PDF Reference Document</p>
                  </div>
                </div>
              )}
            </div>
          </Section>
        )}

        {/* Selected AI Image */}
        {selectedImage && (
          <Section title="Selected AI Preview" icon={<ImageIcon size={16} />}>
            <div className="py-4 space-y-3">
              <div className="relative w-full h-64 rounded-xl overflow-hidden bg-slate-100 shadow-sm">
                <Image
                  src={selectedImage}
                  alt="Selected AI preview"
                  fill
                  className="object-contain"
                  unoptimized
                />
              </div>
              <div className="grid grid-cols-4 gap-2">
                {generatedImages.map((img, idx) => (
                  <div
                    key={idx}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 ${
                      selectedImageIndex === idx
                        ? 'border-amber-500'
                        : 'border-slate-200'
                    }`}
                  >
                    <Image
                      src={img}
                      alt={`Option ${idx + 1}`}
                      fill
                      className="object-cover"
                      unoptimized
                    />
                  </div>
                ))}
              </div>
              <p className="text-xs text-center text-amber-600 font-semibold">
                ★ Image {selectedImageIndex + 1} selected
              </p>
            </div>
          </Section>
        )}

        {/* Error */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex gap-3 p-4 bg-red-50 border border-red-200 rounded-xl"
          >
            <AlertCircle size={18} className="text-red-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-700">Submission Failed</p>
              <p className="text-xs text-red-600 mt-1">{error}</p>
            </div>
          </motion.div>
        )}

        {/* Consent note */}
        <div className="flex gap-2 p-4 bg-slate-50 border border-slate-100 rounded-xl">
          <span className="text-slate-400 shrink-0 mt-0.5">🔒</span>
          <p className="text-xs text-slate-500 leading-relaxed">
            By submitting this inquiry, you agree to be contacted by our sales team regarding your
            product request. Your information is handled securely and never shared with third
            parties.
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-between pt-2">
          <Button
            type="button"
            variant="secondary"
            size="lg"
            onClick={() => setCurrentStep(4)}
            disabled={isSubmitting}
            className="gap-2"
          >
            <ArrowLeft size={18} />
            Back
          </Button>
          <Button
            type="button"
            size="lg"
            onClick={handleSubmit}
            loading={isSubmitting}
            className="gap-2 min-w-44"
          >
            <Send size={18} />
            {isSubmitting ? 'Sending...' : 'Submit Inquiry'}
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
